import styles from './Organization.module.scss';
import Box from '@mui/material/Box';
import TabContext from '@mui/lab/TabContext';
import { useEffect, useState } from 'react';
import { OrganizationGeneral } from './sections/OrganizationGeneral/OrganizationGeneral';
import { getSupplierInfo } from 'store/services/supplier';
import { useAuth } from 'hooks';
import { SupplierInfo } from 'types';
import { OrganizationGroups } from './sections/OrganizationGroups/OrganizationGroups';
import { OrganizationDeliveryTypes } from './sections/OrganizationDeliveryTypes/OrganizationDeliveryTypes';
import { OrganizationDefaults } from './sections/OrganizationDefaults/OrganizationDefaults';
import { OrganizationBackoffice } from './sections/OrganizationBackoffice/OrganizationBackoffice';
import { useLocation } from 'react-router-dom';
import {
    Business,
    Category,
    LocalShipping,
    Settings,
    Sync,
} from '@mui/icons-material';
import { TuTabList } from 'components/common/TuTabList/TuTabList';
import { TuTab } from 'components/common/TuTab/TuTab';
import { TuTabPanel } from 'components/common/TuTabPanel/TuTabPanel';

export interface OrganizationProps {
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

export const Organization = ({ viewAsSupplier }: OrganizationProps) => {
    const location = useLocation();
    const [value, setValue] = useState('general');
    const { user } = useAuth();
    const [supplierInfo, setSupplierInfo] = useState<SupplierInfo>();

    useEffect(() => {
        const urlParts = location.pathname.split('/');
        let currentTab = urlParts.pop() ?? '';

        const organizationTabs = [
            'general',
            'delivery-types',
            'groups',
            'defaults',
        ];

        // Backoffice tab is for admin account only.
        if (user?.protected?.supplierId?.toString() === '0')
            organizationTabs.push('backoffice');

        // If the last part of the URL does not match a valid tab/sub page, default to the general tab.
        if (!organizationTabs.includes(currentTab)) {
            currentTab = 'general';
        }

        setValue(currentTab);
    }, [location.pathname, user]);

    useEffect(() => {
        if (!!user?.protected?.supplierId?.toString()) {
            getSupplierInfo(viewAsSupplier ?? user.protected.supplierId).then(
                (data) => {
                    if (data) setSupplierInfo(data);
                }
            );
        }
    }, [user, viewAsSupplier]);

    const handleChange = (_: React.SyntheticEvent, newValue: string) => {
        setValue(newValue);
        if (!viewAsSupplier) {
            window.history.replaceState(null, '', `/organization/${newValue}`);
        } else {
            window.history.replaceState(
                null,
                '',
                `/supplier-details/${viewAsSupplier}/organization/${newValue}`
            );
        }
    };

    const tabPanelStyles = {
        height: 'calc(100% - 72px)',
        padding: '15px 0',
    };

    return (
        <div data-testid="Organization" className={styles.container}>
            <Box
                sx={{
                    width: '100%',
                    typography: 'body1',
                    height: '100%',
                    position: 'relative',
                }}
            >
                <TabContext value={value}>
                    <Box>
                        <TuTabList
                            variant="scrollable"
                            scrollButtons="auto"
                            onChange={handleChange}
                            aria-label="Organization Tabs"
                        >
                            <TuTab
                                data-testid="OrganizationGeneralTab"
                                label="General"
                                value="general"
                                icon={<Business />}
                            />
                            <TuTab
                                data-testid="OrganizationGroupsTab"
                                label="Groups"
                                value="groups"
                                icon={<Category />}
                            />
                            <TuTab
                                data-testid="OrganizationDeliveryTypesTab"
                                label="Delivery Types"
                                value="delivery-types"
                                icon={<LocalShipping />}
                            />
                            <TuTab
                                data-testid="OrganizationDefaultsTab"
                                label="Defaults"
                                value="defaults"
                                icon={<Settings />}
                            />
                            {user?.protected?.supplierId?.toString() ===
                                '0' && (
                                <TuTab
                                    data-testid="OrganizationBackofficeTab"
                                    label="Backoffice"
                                    value="backoffice"
                                    icon={<Sync />}
                                />
                            )}
                        </TuTabList>
                    </Box>
                    <TuTabPanel value="general" sx={{ ...tabPanelStyles }}>
                        {supplierInfo && (
                            <OrganizationGeneral
                                supplierInfo={supplierInfo}
                                viewAsSupplier={viewAsSupplier}
                                setSupplierInfo={setSupplierInfo}
                            />
                        )}
                    </TuTabPanel>

                    <TuTabPanel value="groups" sx={{ ...tabPanelStyles }}>
                        <OrganizationGroups viewAsSupplier={viewAsSupplier} />
                    </TuTabPanel>

                    <TuTabPanel
                        value="delivery-types"
                        sx={{ ...tabPanelStyles }}
                    >
                        <OrganizationDeliveryTypes
                            viewAsSupplier={viewAsSupplier}
                        />
                    </TuTabPanel>
                    <TuTabPanel value="defaults" sx={{ ...tabPanelStyles }}>
                        <OrganizationDefaults viewAsSupplier={viewAsSupplier} />
                    </TuTabPanel>
                    {user?.protected?.supplierId?.toString() === '0' && (
                        <TuTabPanel
                            value="backoffice"
                            sx={{ ...tabPanelStyles }}
                        >
                            {supplierInfo && (
                                <OrganizationBackoffice
                                    viewAsSupplier={viewAsSupplier}
                                    supplierInfo={supplierInfo}
                                />
                            )}
                        </TuTabPanel>
                    )}
                </TabContext>
            </Box>
        </div>
    );
};
